import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Button,
  cn,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@nexus/ui";
import { mayAnnounceEntryInApp, type ChangelogUserFacingGate } from "@nexus/contracts";
import type {
  ChangelogChannel,
  ChangelogDelivery,
  ChangelogEntry,
  ChangelogPage,
} from "@/features/changelog/api/changelog.api";
import {
  changelogCandidatesQueryOptions,
  changelogDigestsQueryOptions,
  changelogKeys,
  changelogQueryOptions,
  changelogRecipientsQueryOptions,
} from "@/lib/queries";
import {
  useApproveChangelogEntry,
  useRegenerateChangelog,
  useRevokeChangelogApproval,
  useSaveChangelogEntry,
  useSendChangelogEntry,
  useSyncChangelog,
} from "@/features/changelog/hooks/useChangelogActions";
import { useComposeChangelogDigest } from "@/features/changelog/hooks/useChangelogDigestActions";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import {
  useChangelogSettings,
  useSprintConfig,
} from "@/features/changelog/hooks/useChangelogSettings";
import { changelogErrorMessage } from "@/features/changelog/lib/changelog-errors";
import {
  canSend,
  ENTRY_CHANNELS,
  formatDeployedAt,
  getEntryState,
  getStage,
  isChannelInFlight,
  isChannelSent,
  isDeployPickable,
  type ChangelogSendable,
} from "@/features/changelog/lib/changelog-display";
import type { ChangelogInAppBlockedReason } from "./ChangelogSendDialog";
import {
  buildTemplateWindows,
  isWithinWindow,
  startOfDay,
  toDayKey,
  type ChangelogTemplateId,
} from "@/features/changelog/lib/changelog-templates";
import { ChangelogServingNow } from "./ChangelogServingNow";
import { ChangelogNotice } from "./shared/ChangelogNotice";
import { ChangelogComposeSheet } from "./ChangelogComposeSheet";
import { ChangelogDeployActivity } from "./ChangelogDeployActivity";
import { ChangelogDeployRangeFilter } from "./ChangelogDeployRangeFilter";
import { ChangelogDeploysTable } from "./ChangelogDeploysTable";
import { ChangelogDetailSheet } from "./ChangelogDetailSheet";
import { ChangelogSendDialog } from "./ChangelogSendDialog";

type DeployFilter = "all" | "needsReview" | "approved" | "sent";

const DEPLOY_FILTERS: DeployFilter[] = ["all", "needsReview", "approved", "sent"];

/**
 * Every production deploy, newest first — the engineer-facing half of the
 * changelog. Notes are reviewed here, posted to Slack, and optionally shown
 * in-app; what the rest of the company hears about by email is assembled on
 * the Roundups tab.
 */
interface ChangelogDeploysPageProps {
  /** Lets the route move to the roundup that was just composed here. */
  onRoundupComposed?: () => void;
}

export function ChangelogDeploysPage({ onRoundupComposed }: ChangelogDeploysPageProps) {
  const { t, i18n } = useTranslation();
  const permissions = useChangelogPermissions();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<DeployFilter>("all");
  const [range, setRange] = useState<ChangelogTemplateId | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Frozen on mount, so a stretch cannot slide by a day under a tab that has
  // been sitting open since yesterday.
  const [mountedAt] = useState(() => new Date());
  const [openTag, setOpenTag] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  /** Version being posted — independent of the detail sheet so row Post is modal-only. */
  const [sendTag, setSendTag] = useState<string | null>(null);
  // Version tags, so a pick survives the list refetching underneath it.
  const [pickedVersions, setPickedVersions] = useState<ReadonlySet<string>>(new Set());
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isBulkSendOpen, setIsBulkSendOpen] = useState(false);
  /** Frozen when Post opens, so a cache patch mid-loop cannot shrink the queue. */
  const [bulkSendQueue, setBulkSendQueue] = useState<ChangelogEntry[]>([]);

  const { data, isPending, isError, refetch, isFetching } = useQuery({
    ...changelogQueryOptions(),
    enabled: permissions.canList,
  });

  // The send dialog needs to know whether Slack can work at all before an admin
  // picks it, so the list is fetched alongside the dialog rather than inside it.
  const { data: recipients } = useQuery({
    ...changelogRecipientsQueryOptions(),
    enabled: permissions.canListRecipients && (sendTag !== null || isBulkSendOpen),
  });

  // The activity chart marks deploys a roundup has already taken, which only
  // the digest list knows.
  const { data: digestData } = useQuery({
    ...changelogDigestsQueryOptions(),
    enabled: permissions.canListDigests,
  });

  // Which deploys a roundup could still take, and the item ids that picking one
  // stands for. Fetched with the tab rather than with the compose sheet: it is
  // what decides whether a row's checkbox works at all.
  const { data: candidates, isFetching: isLoadingCandidates } = useQuery({
    ...changelogCandidatesQueryOptions(),
    enabled: permissions.canCompose && permissions.canListCandidates,
  });

  const { settings, isPending: isSettingsPending } = useChangelogSettings();
  const sprint = useSprintConfig();

  const sync = useSyncChangelog();
  const compose = useComposeChangelogDigest();
  const regenerate = useRegenerateChangelog();
  const save = useSaveChangelogEntry();
  const approve = useApproveChangelogEntry();
  const revoke = useRevokeChangelogApproval();
  const send = useSendChangelogEntry();

  // Opening the tab is what detects a new deploy, so sync once per mount rather
  // than on every render or refocus — a second sync would re-read GitHub for no
  // new information.
  const hasSynced = useRef(false);
  const isConfigured = data?.configured ?? false;
  const canSync = permissions.canSync;
  const syncNow = sync.mutate;
  const autoSync = settings?.autoSyncOnOpen ?? true;

  useEffect(() => {
    // Waiting on the settings costs one round trip and buys the guarantee that
    // a team who switched auto-sync off never sees it fire once more on open.
    if (hasSynced.current || isSettingsPending || !autoSync || !isConfigured || !canSync) return;
    hasSynced.current = true;
    syncNow();
  }, [autoSync, isSettingsPending, isConfigured, canSync, syncNow]);

  const entries = useMemo(() => data?.entries ?? [], [data?.entries]);
  const activeWindow = useMemo(() => {
    if (range === null) return null;
    return (
      buildTemplateWindows(mountedAt, i18n.language, sprint).find(
        (template) => template.id === range,
      )?.window ?? null
    );
  }, [range, mountedAt, i18n.language, sprint]);

  const inState = useMemo(
    () => entries.filter((entry) => matchesFilter(entry, filter)),
    [entries, filter],
  );
  // The chart plots what the state and range filters left behind, so the two
  // read as one view; picking a day then narrows the table without redrawing
  // the chart.
  const inRange = useMemo(
    () =>
      activeWindow === null
        ? inState
        : inState.filter((entry) => isWithinWindow(entry.deployedAt, activeWindow)),
    [inState, activeWindow],
  );
  const rows = useMemo(
    () =>
      selectedDay === null
        ? inRange
        : inRange.filter((entry) => dayKey(entry.deployedAt) === selectedDay),
    [inRange, selectedDay],
  );
  const openEntry = entries.find((entry) => entry.versionTag === openTag) ?? null;
  const sendEntry = entries.find((entry) => entry.versionTag === sendTag) ?? null;
  const sendInAppBlockedReason = entryInAppBlockedReason(
    settings?.userFacingAnnouncements,
    sendEntry?.significance,
  );

  // Null until the roundups are actually readable — an empty map would tell
  // the chart every deploy is un-announced.
  const roundupTitleByVersion = useMemo(() => {
    if (digestData === undefined) return null;
    const titles = new Map<string, string>();
    for (const digest of digestData.digests) {
      for (const covered of digest.entries) {
        titles.set(covered.versionTag, digest.title);
      }
    }
    return titles;
  }, [digestData]);

  // A deploy with nothing left to say has nothing to contribute to a roundup,
  // so it is not offered — same rule the compose sheet applies to its own list.
  const itemIdsByVersion = useMemo(() => {
    const byVersion = new Map<string, string[]>();
    for (const candidate of candidates?.entries ?? []) {
      if (candidate.items.length === 0) continue;
      byVersion.set(
        candidate.versionTag,
        candidate.items.map((item) => item.id),
      );
    }
    return byVersion;
  }, [candidates?.entries]);

  const composableVersions = useMemo(
    () => new Set(itemIdsByVersion.keys()),
    [itemIdsByVersion],
  );

  const canCompose = permissions.canCompose && permissions.canListCandidates;

  const pickedSendable = useMemo(
    () =>
      entries.filter(
        (entry) => pickedVersions.has(entry.versionTag) && canSend(entry, ENTRY_CHANNELS),
      ),
    [entries, pickedVersions],
  );

  const pickedComposableCount = useMemo(
    () => [...pickedVersions].filter((version) => composableVersions.has(version)).length,
    [pickedVersions, composableVersions],
  );

  const bulkInAppBlockedReason = useMemo(() => {
    if (bulkSendQueue.length === 0) return null;
    const reasons = bulkSendQueue.map((entry) =>
      entryInAppBlockedReason(settings?.userFacingAnnouncements, entry.significance),
    );
    if (reasons.every((reason) => reason !== null)) return reasons[0];
    return null;
  }, [bulkSendQueue, settings?.userFacingAnnouncements]);

  const pickedItemIds = useMemo(
    () => [...pickedVersions].flatMap((version) => itemIdsByVersion.get(version) ?? []),
    [pickedVersions, itemIdsByVersion],
  );

  const isBusy = regenerate.isPending || approve.isPending || revoke.isPending || send.isPending;
  const actionError = sync.error ?? regenerate.error ?? approve.error ?? revoke.error ?? send.error;

  /** A day picked inside the old stretch would filter the table down to nothing. */
  function handleRangeChange(next: ChangelogTemplateId | null) {
    setRange(next);
    setSelectedDay(null);
  }

  function handleApprove(entry: ChangelogEntry) {
    approve.mutate(
      { versionTag: entry.versionTag, expectedUpdatedAt: entry.updatedAt },
      {
        onSuccess: () => {
          // Approval is reversible until the first channel is queued, so it is
          // applied immediately and offered back rather than guarded by a
          // confirmation nobody reads.
          toast.success(
            t("admin.changelog.approvedToast", "{{version}} approved", {
              version: entry.versionTag,
            }),
            {
              action: {
                label: t("admin.changelog.undo", "Undo"),
                onClick: () => revoke.mutate(entry.versionTag),
              },
            },
          );
        },
      },
    );
  }

  function togglePick(entry: ChangelogEntry) {
    setPickedVersions((current) => {
      const next = new Set(current);
      if (next.has(entry.versionTag)) next.delete(entry.versionTag);
      else next.add(entry.versionTag);
      return next;
    });
  }

  /** Acts on what the filters left on screen, which is the only list an admin can see. */
  function togglePickAll() {
    const onScreen = rows
      .filter((entry) =>
        isDeployPickable(entry, {
          canCompose,
          canSendEntries: permissions.canSend,
          composableVersions,
        }),
      )
      .map((entry) => entry.versionTag);
    const isAllPicked = onScreen.every((version) => pickedVersions.has(version));

    setPickedVersions((current) => {
      const next = new Set(current);
      for (const version of onScreen) {
        if (isAllPicked) next.delete(version);
        else next.add(version);
      }
      return next;
    });
  }

  function handleCompose(input: { title: string; itemIds: string[] }) {
    compose.mutate(input, {
      onSuccess: () => {
        setIsComposeOpen(false);
        setPickedVersions(new Set());
        toast.success(
          t("admin.changelog.deploys.composedToast", "{{title}} is being written", {
            title: input.title,
          }),
        );
        // Approving and sending it happens on the other tab, so that is where
        // the admin is left rather than back on a list of ticks they just spent.
        onRoundupComposed?.();
      },
    });
  }

  function handleSend(versionTag: string, channels: ChangelogChannel[], force = false) {
    send.mutate(
      { versionTag, channels, force },
      {
        onSuccess: () => {
          setSendTag(null);
          toast.success(
            t("admin.changelog.sentToast", "{{version}} is on its way", { version: versionTag }),
          );
        },
      },
    );
  }

  async function handleBulkSend(channels: ChangelogChannel[]) {
    let sent = 0;
    try {
      for (const queued of bulkSendQueue) {
        const live =
          queryClient
            .getQueryData<ChangelogPage>(changelogKeys.list)
            ?.entries.find((entry) => entry.versionTag === queued.versionTag) ?? queued;
        const remaining = channels.filter((channel) => {
          if (isChannelSent(live, channel) || isChannelInFlight(live, channel)) return false;
          if (
            channel === "IN_APP" &&
            entryInAppBlockedReason(settings?.userFacingAnnouncements, live.significance) !== null
          ) {
            return false;
          }
          return true;
        });
        if (remaining.length === 0) continue;
        await send.mutateAsync({ versionTag: live.versionTag, channels: remaining });
        sent += 1;
      }
    } catch {
      return;
    }
    if (sent === 0) return;
    setIsBulkSendOpen(false);
    setBulkSendQueue([]);
    toast.success(
      t("admin.changelog.deploys.bulkSentToast", "{{count}} on their way", { count: sent }),
    );
  }

  function closeEditor() {
    if (
      isEditorDirty &&
      !window.confirm(
        t("admin.changelog.discardConfirm", "Discard your unsaved changes to these notes?"),
      )
    ) {
      return false;
    }
    setIsEditing(false);
    setIsEditorDirty(false);
    save.reset();
    return true;
  }

  function handleOpenChange(open: boolean) {
    if (open) return;
    if (isEditing && !closeEditor()) return;
    setOpenTag(null);
  }

  if (!permissions.canList) {
    return (
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        {t("admin.changelog.noAccess", "You do not have access to the changelog.")}
      </p>
    );
  }

  return (
    <>
      <ChangelogServingNow
        entries={data?.entries ?? []}
        isListReady={!isPending && !isError && Boolean(data?.configured)}
        onOpenVersion={(versionTag) => {
          setOpenTag(versionTag);
          setIsEditing(false);
          setIsEditorDirty(false);
          save.reset();
        }}
      />
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Select value={filter} onValueChange={(value) => setFilter(value as DeployFilter)}>
          <SelectTrigger
            aria-label={t("admin.changelog.filters.label", "Filter releases")}
            className={cn(
              "h-8 w-[170px] rounded-sm text-xs",
              filter !== "all" && "border-primary/40 bg-primary/5 text-foreground",
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEPLOY_FILTERS.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`admin.changelog.filters.${value}`, { defaultValue: value })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ChangelogDeployRangeFilter
          entries={inState}
          now={mountedAt}
          value={range}
          onChange={handleRangeChange}
        />

        <span className="flex-1" />

        {pickedVersions.size > 0 ? (
          <>
            <span className="text-xs tabular-nums text-muted-foreground">
              {t("admin.changelog.deploys.picked", "{{count}} picked", {
                count: pickedVersions.size,
              })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-sm text-xs"
              onClick={() => setPickedVersions(new Set())}
            >
              {t("admin.changelog.deploys.clearPicked", "Clear")}
            </Button>
            {permissions.canSend && pickedSendable.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-sm text-xs"
                disabled={isBusy}
                onClick={() => {
                  setSendTag(null);
                  setBulkSendQueue(pickedSendable);
                  setIsBulkSendOpen(true);
                }}
              >
                {t("admin.changelog.deploys.bulkPost", "Post")}
              </Button>
            ) : null}
            {canCompose && pickedComposableCount > 0 ? (
              <Button
                size="sm"
                iconStart="plus"
                className="h-8 rounded-sm text-xs"
                onClick={() => setIsComposeOpen(true)}
              >
                {t("admin.changelog.roundups.compose", "Compose roundup")}
              </Button>
            ) : null}
          </>
        ) : (
          <span className="text-xs tabular-nums text-muted-foreground">
            {t("admin.changelog.deployCount", { count: rows.length })}
          </span>
        )}

        {permissions.canSync ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-sm text-xs"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            <Icon
              name="refresh-cw"
              size="xs"
              color="inherit"
              className={cn(sync.isPending && "animate-spin motion-reduce:animate-none")}
              aria-hidden
            />
            {t("admin.changelog.checkForDeploys", "Check for releases")}
          </Button>
        ) : null}
      </div>

      {data?.configured && inRange.length > 0 ? (
        <ChangelogDeployActivity
          entries={inRange}
          window={activeWindow}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          roundupTitleByVersion={roundupTitleByVersion}
          pickedVersions={pickedVersions}
        />
      ) : null}

      {actionError ? (
        <div className="shrink-0 rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
          {changelogErrorMessage(
            actionError,
            t("admin.changelog.actionError", "That did not work. Try again."),
          )}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border bg-card">
        {isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
            <p className="m-0 text-sm text-muted-foreground">
              {t("admin.changelog.loadError", "Could not load the changelog.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t("admin.changelog.retry", "Try again")}
            </Button>
          </div>
        ) : !data.configured ? (
          <ChangelogNotice
            title={t("admin.changelog.notConfiguredTitle", "Release history is switched off")}
            description={t(
              "admin.changelog.notConfigured",
              "The changelog reads release history from GitHub. Set API_GITHUB_TOKEN on the API service to turn it on.",
            )}
          />
        ) : rows.length === 0 ? (
          <ChangelogNotice
            title={
              entries.length === 0
                ? t("admin.changelog.emptyTitle", "No production releases recorded yet")
                : t("admin.changelog.noMatchTitle", "Nothing matches these filters")
            }
            description={
              entries.length === 0
                ? t(
                    "admin.changelog.empty",
                    "Every production release shows up here with notes written from its commits.",
                  )
                : t(
                    "admin.changelog.noMatch",
                    "Try a wider stretch, or a different state.",
                  )
            }
            action={
              entries.length === 0 ? null : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilter("all");
                    setRange(null);
                    setSelectedDay(null);
                  }}
                >
                  {t("admin.changelog.filters.all", "All releases")}
                </Button>
              )
            }
          />
        ) : (
          <ChangelogDeploysTable
            entries={rows}
            selectedTag={openTag}
            roundupTitleByVersion={roundupTitleByVersion}
            canApproveEntries={permissions.canApprove}
            canSendEntries={permissions.canSend}
            canCompose={canCompose}
            composableVersions={composableVersions}
            pickedVersions={pickedVersions}
            isBusy={isBusy}
            onSelect={(entry) => {
              setOpenTag(entry.versionTag);
              setIsEditing(false);
              setIsEditorDirty(false);
              save.reset();
            }}
            onApprove={handleApprove}
            onOpenSend={(entry) => setSendTag(entry.versionTag)}
            onTogglePick={togglePick}
            onTogglePickAll={togglePickAll}
          />
        )}
      </div>

      {isFetching && !isPending ? (
        <span className="sr-only" aria-live="polite">
          {t("admin.changelog.refreshing", "Refreshing releases")}
        </span>
      ) : null}

      {openEntry ? (
        <ChangelogDetailSheet
          kind="deploy"
          isOpen
          onOpenChange={handleOpenChange}
          title={openEntry.versionTag}
          isTitleMono
          meta={
            <>
              <span className="font-mono">{openEntry.shortSha}</span>
              {" · "}
              <span className="font-mono">{openEntry.branch}</span>
              {" · "}
              {openEntry.deployedBy
                ? t("admin.changelog.deployedByAt", "released {{at}} by {{by}}", {
                    at: formatDeployedAt(openEntry.deployedAt, i18n.language),
                    by: openEntry.deployedBy,
                  })
                : t("admin.changelog.deployedAt", "released {{at}}", {
                    at: formatDeployedAt(openEntry.deployedAt, i18n.language),
                  })}
            </>
          }
          target={openEntry}
          extra={
            openEntry.commitCount > 0 && !openEntry.editedByName ? (
              <p className="m-0 flex items-center gap-1.5 text-[11px] text-[var(--ai-text)]">
                <Icon name="sparkles" size="xs" colorVar="--ai-text" aria-hidden />
                {t("admin.changelog.aiNote", { count: openEntry.commitCount })}
              </p>
            ) : null
          }
          channels={ENTRY_CHANNELS}
          capabilities={{
            edit: permissions.canEdit,
            approve: permissions.canApprove,
            revokeApproval: permissions.canRevokeApproval,
            send: permissions.canSend,
            regenerate: permissions.canRegenerate,
            discard: false,
          }}
          isBusy={isBusy}
          isEditing={isEditing}
          isDirty={isEditorDirty}
          isSaving={save.isPending}
          saveError={
            save.error
              ? changelogErrorMessage(
                  save.error,
                  t("admin.changelog.saveError", "Could not save these notes."),
                )
              : null
          }
          onStartEdit={() => {
            setIsEditing(true);
            setIsEditorDirty(false);
            save.reset();
          }}
          onCancelEdit={closeEditor}
          onDirtyChange={setIsEditorDirty}
          onSave={(input) =>
            save.mutate(
              {
                versionTag: openEntry.versionTag,
                headline: input.headline,
                sections: input.sections,
                significance: input.significance,
                expectedUpdatedAt: openEntry.updatedAt,
              },
              {
                onSuccess: () => {
                  setIsEditing(false);
                  setIsEditorDirty(false);
                },
              },
            )
          }
          onRegenerate={() => regenerate.mutate(openEntry.versionTag)}
          onApprove={() => handleApprove(openEntry)}
          onRevokeApproval={() => revoke.mutate(openEntry.versionTag)}
          onOpenSend={() => setSendTag(openEntry.versionTag)}
          onRetryChannel={(channel, force) => handleSend(openEntry.versionTag, [channel], force)}
        />
      ) : null}

      {isComposeOpen ? (
        <ChangelogComposeSheet
          isOpen
          candidates={candidates}
          initialItemIds={pickedItemIds}
          isLoading={isLoadingCandidates}
          isComposing={compose.isPending}
          composeError={
            compose.error
              ? changelogErrorMessage(
                  compose.error,
                  t("admin.changelog.compose.error", "Could not start the merge."),
                )
              : null
          }
          onOpenChange={setIsComposeOpen}
          onCompose={handleCompose}
        />
      ) : null}

      {sendEntry ? (
        <ChangelogSendDialog
          targetId={sendEntry.versionTag}
          title={t("admin.changelog.send.title", "Send {{version}} release notes", {
            version: sendEntry.versionTag,
          })}
          target={sendEntry}
          channels={ENTRY_CHANNELS}
          isOpen
          isSending={send.isPending}
          sendError={
            send.error
              ? changelogErrorMessage(
                  send.error,
                  t("admin.changelog.send.error", "Could not start the delivery."),
                )
              : null
          }
          recipients={recipients}
          inAppBlockedReason={sendInAppBlockedReason}
          onOpenChange={(open) => {
            if (!open) setSendTag(null);
          }}
          onSend={(channels) => handleSend(sendEntry.versionTag, channels)}
        />
      ) : null}

      {isBulkSendOpen && bulkSendQueue.length > 0 ? (
        <ChangelogSendDialog
          targetId={`bulk:${bulkSendQueue.map((entry) => entry.versionTag).join(",")}`}
          title={t("admin.changelog.deploys.bulkSendTitle", "Send {{count}} release notes", {
            count: bulkSendQueue.length,
          })}
          target={bulkSendTarget(bulkSendQueue)}
          channels={ENTRY_CHANNELS}
          isOpen
          isSending={send.isPending}
          sendError={
            send.error
              ? changelogErrorMessage(
                  send.error,
                  t("admin.changelog.send.error", "Could not start the delivery."),
                )
              : null
          }
          recipients={recipients}
          inAppBlockedReason={bulkInAppBlockedReason}
          onOpenChange={(open) => {
            if (!open) {
              setIsBulkSendOpen(false);
              setBulkSendQueue([]);
            }
          }}
          onSend={(channels) => void handleBulkSend(channels)}
        />
      ) : null}
    </>
  );
}

/** Local calendar date, matching how the chart buckets its columns. */
function dayKey(deployedAt: string): string {
  return toDayKey(startOfDay(new Date(deployedAt)));
}

function matchesFilter(entry: ChangelogEntry, filter: DeployFilter): boolean {
  if (filter === "all") return true;
  const stage = getStage(entry);
  if (filter === "sent") return stage === "sent";
  if (filter === "approved") return stage === "approved";
  // "Needs review" is the queue: notes that exist, say something, and nobody
  // has signed off yet. A failed generation belongs here too — it needs a human.
  const state = getEntryState(entry);
  return state === "failed" || (stage === "draft" && entry.status === "READY");
}

/** Mirrors `mayAnnounceEntryInApp` so the dialog explains a refusal before Send. */
function entryInAppBlockedReason(
  gate: ChangelogUserFacingGate | undefined,
  significance: ChangelogEntry["significance"] | undefined,
): ChangelogInAppBlockedReason | null {
  if (gate === undefined || significance === undefined) return null;
  if (mayAnnounceEntryInApp(gate, significance)) return null;
  return gate === "HOLD" ? "inAppOnHold" : "inAppBigNewsOnly";
}

/**
 * A channel is pickable when at least one queued deploy can still take it.
 * Fabricating an empty deliveries list would offer Slack after every deploy
 * had already posted there.
 */
function bulkSendTarget(sendable: ChangelogEntry[]): ChangelogSendable {
  const deliveries: ChangelogDelivery[] = ENTRY_CHANNELS.flatMap((channel) => {
    const someoneCanTake = sendable.some(
      (entry) => !isChannelSent(entry, channel) && !isChannelInFlight(entry, channel),
    );
    if (someoneCanTake) return [];
    const allSent = sendable.every((entry) => isChannelSent(entry, channel));
    return [
      {
        channel,
        status: allSent ? "SENT" : "SENDING",
        recipientCount: null,
        note: null,
        sentAt: null,
        requestedByName: null,
        isStuck: false,
      },
    ];
  });

  return {
    status: "READY",
    approvedAt: sendable[0]?.approvedAt ?? new Date().toISOString(),
    itemCount: Math.max(
      1,
      sendable.reduce((count, entry) => count + entry.itemCount, 0),
    ),
    isGenerationStuck: false,
    deliveries,
  };
}
